import React from 'react';
import { baptismConfirmations as api } from '../../api/endpoints';
import { formatDate } from '../../utils/format';
import { FormLayout, Input } from '../../components/ui';
import type { RegisterConfig } from './registerConfig';
import RegisterMemberField from './MemberField';
import type { BaptismConfirmation, Member } from '../../types';

const EMPTY = {
  confirmationNo: '', confirmationRegisterNo: '', memberId: '', personName: '',
  baptismReference: '', confirmationDate: '', bishopName: '', church: '',
  certificateNo: '', remarks: '',
  /** Form-local: the picked member, for the warnings. Stripped before saving. */
  _member: null as Member | null
};

type Form = typeof EMPTY;

const Certificate = (r: BaptismConfirmation) => (
  <div>
    <p>
      This is to certify that <strong>{r.personName || r.memberId || '—'}</strong> was
      confirmed in the faith on <strong>{formatDate(r.confirmationDate)}</strong> at{' '}
      <strong>{r.church || '—'}</strong>, by the laying on of hands of{' '}
      <strong>{r.bishopName || '—'}</strong>.
    </p>

    <div style={{ marginTop: '20px' }}>
      {r.memberId && <div><strong>Member ID:</strong> {r.memberId}</div>}
      <div><strong>Baptism Reference:</strong> {r.baptismReference || '—'}</div>
      <div><strong>Certificate No:</strong> {r.certificateNo || '—'}</div>
      {r.remarks && <div><strong>Remarks:</strong> {r.remarks}</div>}
    </div>

    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '70px' }}>
      <div style={{ borderTop: '1px solid #000', width: '220px', textAlign: 'center', paddingTop: '5px' }}>Bishop</div>
      <div style={{ borderTop: '1px solid #000', width: '220px', textAlign: 'center', paddingTop: '5px' }}>Church Seal</div>
    </div>
  </div>
);

/**
 * Questions worth asking before the entry is filed, none of which should stop
 * it: a member who transferred in was baptised somewhere this parish has no
 * record of, and a correction to an existing confirmation is a normal edit.
 */
const warn = (f: Form): string | null => {
  const m = f._member;
  if (!m) return null;

  const notes: string[] = [];
  if (!m.baptismDate) {
    notes.push(
      `${m.name || m.memberId} has no baptism on file — confirmation normally follows baptism. `
      + 'That is expected for a member who transferred in from another church.'
    );
  }
  if (m.confirmationDate && m.confirmationDate !== f.confirmationDate) {
    notes.push(
      `${m.name || m.memberId} is already recorded as confirmed on ${formatDate(m.confirmationDate)}. `
      + 'Saving will replace that date on the member record.'
    );
  }
  return notes.length ? notes.join(' ') : null;
};

const config: RegisterConfig<BaptismConfirmation, Form> = {
  icon: '🕊️',
  title: 'Baptism Confirmation Register',
  prefix: 'CNF',
  label: 'Confirmation Entry',
  resource: api,
  emptyForm: EMPTY,
  toForm: (r) => ({
    ...EMPTY,
    confirmationNo: r.confirmationNo || '', confirmationRegisterNo: r.confirmationRegisterNo || '',
    memberId: r.memberId || '', personName: r.personName || '',
    baptismReference: r.baptismReference || '',
    confirmationDate: r.confirmationDate || '', bishopName: r.bishopName || '',
    church: r.church || '', certificateNo: r.certificateNo || '', remarks: r.remarks || ''
  }),
  validate: (f) => {
    if (!f.memberId.trim()) return 'Choose the member being confirmed.';
    if (!f.confirmationDate) return 'The date of confirmation is required.';
    if (!f.bishopName.trim()) return 'The officiating bishop is required.';
    const born = f._member?.dateOfBirth;
    if (born && f.confirmationDate < born) {
      return 'The confirmation cannot be dated before the member was born.';
    }
    return null;
  },
  warn,
  rowDate: (r) => r.confirmationDate,
  searchKeys: ['recordId', 'memberId', 'personName', 'confirmationNo', 'confirmationRegisterNo', 'bishopName', 'certificateNo'],
  searchPlaceholder: 'Search by name, Member ID, Confirmation No or Bishop...',
  columns: [
    {
      key: 'recordId',
      header: 'Date & Conf. No.',
      nowrap: true,
      render: (r: BaptismConfirmation) => (
        <>
          <strong>{formatDate(r.confirmationDate)}</strong>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.confirmationNo || r.recordId}</div>
        </>
      )
    },
    {
      key: 'personName',
      header: 'Member',
      render: (r: BaptismConfirmation) => (
        <>
          <strong>{r.personName || r.memberId || '-'}</strong>
          {r.personName && <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.memberId}</div>}
        </>
      )
    },
    { key: 'baptismReference', header: 'Baptism Ref.', render: (r: BaptismConfirmation) => r.baptismReference || '-' },
    {
      key: 'bishopName',
      header: 'Bishop & Church',
      render: (r: BaptismConfirmation) => (
        <>
          <div>{r.bishopName || '-'}</div>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.church || '-'}</div>
        </>
      )
    },
    { key: 'certificateNo', header: 'Cert No.', render: (r: BaptismConfirmation) => r.certificateNo || '-' }
  ],
  printModule: 'Baptism',
  printDocumentType: 'CONFIRMATION_CERTIFICATE',
  listDocumentType: 'CONFIRMATION_REGISTER',
  printData: (r: any) => ({ ...r, baptismDate: formatDate(r.date || r.confirmationDate) }),
  renderDocument: Certificate,
  fields: (form, setForm, ctx) => (
    <>
      <FormLayout.Grid>
        <RegisterMemberField
          form={form}
          setForm={setForm}
          idKey="memberId"
          memberKey="_member"
          label="Member Being Confirmed"
          hint="Pick the member — the baptism reference fills in from their record."
          required
          fullWidth
          isDarkMode={ctx.isDarkMode}
          // The register stores the id; the baptism reference is what the
          // certificate quotes, so it is offered from the member's own date
          // rather than looked up by hand in the baptism book.
          autofill={(member) => ({
            personName: member?.name || '',
            baptismReference: member?.baptismDate
              ? `Baptised ${formatDate(member.baptismDate)}`
              : ''
          })}
        />
        <Input
          label="Date of Confirmation" type="date" required
          value={form.confirmationDate} onChange={(e) => setForm({ ...form, confirmationDate: e.target.value })}
        />
        <Input
          label="Officiating Bishop" required placeholder="e.g. Rt. Rev. Bishop"
          value={form.bishopName} onChange={(e) => setForm({ ...form, bishopName: e.target.value })}
        />
        <Input
          label="Church" placeholder="Where the confirmation took place"
          value={form.church} onChange={(e) => setForm({ ...form, church: e.target.value })}
        />
        <Input
          label="Baptism Reference" fullWidth
          placeholder="The baptism register entry this follows from"
          value={form.baptismReference} onChange={(e) => setForm({ ...form, baptismReference: e.target.value })}
        />
      </FormLayout.Grid>

      <FormLayout.Section title="Register Details" />
      <FormLayout.Grid>
        <Input
          label="Confirmation Number" placeholder="e.g. CN-2026-007"
          value={form.confirmationNo} onChange={(e) => setForm({ ...form, confirmationNo: e.target.value })}
        />
        <Input
          label="Confirmation Register No." placeholder="e.g. Vol 2, Page 15"
          value={form.confirmationRegisterNo} onChange={(e) => setForm({ ...form, confirmationRegisterNo: e.target.value })}
        />
        <Input
          label="Certificate Number" placeholder="e.g. CC-2026-007"
          value={form.certificateNo} onChange={(e) => setForm({ ...form, certificateNo: e.target.value })}
        />
        <Input
          label="Remarks" type="textarea" rows={2} fullWidth
          value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
        />
      </FormLayout.Grid>
    </>
  )
};

export { config as confirmationConfig };
